// PdfThumbnail — render page 1 of a PDF to a canvas.
//
// Accepts a `File` (the upload-modal pre-flight preview) OR a remote `url`
// (cards in the media library, post-upload preview dialog). Uses pdfjs-dist
// so the result is a real image — independent of any browser PDF viewer
// chrome — and the same across Chrome / Firefox / Safari / WebView. A
// module-level data-URL cache keyed by URL keeps repeat mounts of the same
// PDF (re-renders of the media grid as it sorts / paginates) from refetching
// and re-rasterising every time.

import { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { FileText, FileWarning } from 'lucide-react'
import * as pdfjsLib from 'pdfjs-dist'
// Vite ?worker virtual import — see vite/client types for the declaration.
import PdfJsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'

// pdfjs is loud if workerPort is set twice. Gate on the existing value so
// re-evaluation under HMR doesn't crash.
if (!pdfjsLib.GlobalWorkerOptions.workerPort) {
  pdfjsLib.GlobalWorkerOptions.workerPort = new PdfJsWorker()
}

// Module-level cache: { url → data URL of first page }. We only cache by URL,
// not by File, because File objects are one-shot blobs that are revoked the
// moment the upload modal closes — there's no benefit to caching them.
const urlThumbCache = new Map<string, string>()

type PdfSource = { kind: 'file'; file: File } | { kind: 'url'; url: string }

interface Props {
  // Pick exactly one — the PDF to render.
  file?:   File
  url?:    string
  // Optional CSS classes layered on the outer wrapper. The wrapper is
  // `position: relative` so the loading / error overlays can absolutely-fill it.
  className?: string
  // Width to rasterise the first page at, in CSS pixels. Higher = sharper but
  // slower. The component multiplies by DPR for hi-DPI screens automatically.
  // Defaults match a typical 4:3 grid card (320px) so we don't blow the cache
  // out on small thumbnails.
  targetWidth?: number
  // Fit mode: 'cover' fills the wrapper (cropping if needed), 'contain' shows
  // the whole page letterboxed. Cards use 'cover' so the page edges line up
  // with the tile; previews use 'contain' so the whole page is visible.
  fit?: 'cover' | 'contain'
}

export default function PdfThumbnail({
  file, url, className, targetWidth = 320, fit = 'contain',
}: Props) {
  const source = useMemo<PdfSource | null>(() => {
    if (file) return { kind: 'file', file }
    if (url)  return { kind: 'url',  url  }
    return null
  }, [file, url])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  // When we hit the URL cache we paint via an <img> instead of pdfjs so the
  // tile renders instantly and skips the worker round-trip.
  const [cachedDataUrl, setCachedDataUrl] = useState<string | null>(() =>
    source?.kind === 'url' ? urlThumbCache.get(source.url) ?? null : null,
  )

  useEffect(() => {
    if (!source) { setStatus('error'); return }

    // Cached fast-path: paint the saved data URL and skip pdfjs entirely.
    if (source.kind === 'url') {
      const hit = urlThumbCache.get(source.url)
      if (hit) {
        setCachedDataUrl(hit)
        setStatus('ready')
        return
      }
    }
    setCachedDataUrl(null)

    let cancelled = false
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null

    async function render() {
      setStatus('loading')
      try {
        // Source: File goes through arrayBuffer; URL is handed to pdfjs which
        // does its own ranged GETs. pdfjs respects same-origin CORS — backend
        // serves media with permissive CORS so the URL path Just Works.
        let docInput: any
        if (source!.kind === 'file') {
          const buffer = await source!.file.arrayBuffer()
          if (cancelled) return
          docInput = { data: new Uint8Array(buffer) }
        } else {
          docInput = { url: source!.url }
        }

        loadingTask = pdfjsLib.getDocument(docInput)
        const pdfDoc = await loadingTask.promise
        if (cancelled) return

        const page = await pdfDoc.getPage(1)
        if (cancelled) return

        const canvas = canvasRef.current
        if (!canvas) return

        // Rasterise at DPR-aware scale so hi-DPI displays stay crisp, but
        // cap DPR at 2 to keep the canvas footprint reasonable for grids.
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        const baseViewport = page.getViewport({ scale: 1 })
        const scale = (targetWidth / baseViewport.width) * dpr
        const viewport = page.getViewport({ scale })

        canvas.width  = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        canvas.style.aspectRatio = `${baseViewport.width} / ${baseViewport.height}`

        await page.render({ canvas, viewport }).promise
        if (cancelled) return

        // Cache by URL for the rest of the session. File previews aren't
        // cached — the blob URL would have been revoked anyway.
        if (source!.kind === 'url') {
          try {
            urlThumbCache.set(source!.url, canvas.toDataURL('image/jpeg', 0.85))
          } catch {
            /* tainted canvas (cross-origin without CORS headers) — skip cache */
          }
        }

        setStatus('ready')
      } catch (e) {
        if (!cancelled) {
          // Previously console.error-only — a failure here was invisible
          // outside a dev console (production/Sentry never heard about it),
          // which is exactly why "some PDFs never get a thumbnail" was
          // undiagnosable. Now reported like every other content-render
          // failure in the app.
          console.error('PDF thumbnail render failed', e)
          try {
            Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
              tags: { content: 'pdf-thumbnail' },
              extra: { source: source!.kind === 'url' ? source!.url : '(file upload)' },
            })
          } catch { /* Sentry not configured — fall through */ }
          setStatus('error')
        }
      }
    }
    render()

    return () => {
      cancelled = true
      // PDFDocumentLoadingTask.destroy() aborts inflight requests AND tears
      // down the worker-side document. PDFDocumentProxy itself has no
      // destroy() in pdfjs-dist v6, so this single call is the full cleanup.
      try { loadingTask?.destroy() } catch { /* noop */ }
    }
  }, [source, targetWidth])

  // Object-fit style applied to whichever leaf renders — keeps the tile
  // looking consistent whether we hit the cache (img) or fresh-render (canvas).
  const fitClass = fit === 'cover' ? 'object-cover' : 'object-contain'

  // The overlay shows whenever we don't have a finished render yet — both
  // the in-flight `loading` state and the terminal `error` state — but the
  // two are now visually DISTINCT (a plain document icon vs. a warning
  // variant), not the same animated mark for both. The old pulsing WeTook
  // logo made a genuinely-failed render (CORS-blocked S3, malformed file,
  // blocked worker) indistinguishable from "still loading" — it would just
  // pulse forever with no way to tell the two apart, which is exactly why
  // "some PDFs never got a thumbnail" went unnoticed for so long.
  const showOverlay = status !== 'ready'

  return (
    <div className={`relative bg-muted overflow-hidden ${className ?? ''}`}>
      {showOverlay && <PdfThumbnailIcon errored={status === 'error'} />}
      {cachedDataUrl ? (
        <img
          src={cachedDataUrl}
          alt=""
          className={`w-full h-full ${fitClass}`}
          style={{ visibility: status === 'ready' ? 'visible' : 'hidden' }}
        />
      ) : (
        <canvas
          ref={canvasRef}
          className={`w-full h-full ${fitClass}`}
          style={{ visibility: status === 'ready' ? 'visible' : 'hidden' }}
        />
      )}
    </div>
  )
}

// Static placeholder — no animation. A plain document glyph (matching the
// FileText/orange convention used for PDFs everywhere else in the app —
// MediaPage, LayoutDetailsPage, AssetPreviewDialog, etc.) while rasterising;
// swaps to a distinct warning glyph if the render terminally failed, so a
// broken thumbnail reads as "this failed" rather than "still working on it".
function PdfThumbnailIcon({ errored }: { errored: boolean }) {
  const Icon = errored ? FileWarning : FileText
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-muted">
      <Icon
        className={`w-[35%] h-[35%] ${errored ? 'text-destructive/70' : 'text-orange-500/70'}`}
        aria-hidden
      />
    </div>
  )
}
